/**
 * Native lifecycle wiring for the Capacitor shell.
 *
 * Centralises every "what happens when the user backgrounds / resumes /
 * presses back / opens a deep link" decision so route components don't
 * each subscribe to `App.addListener`. Loaded exactly once from
 * `main.tsx` on app boot.
 *
 * No-ops on web — every helper here short-circuits via `isNative()` so
 * the import remains free for the browser build.
 *
 * Each subsystem returns a teardown function. We aggregate them into a
 * single cleanup and expose `installNativeLifecycle()` which itself
 * returns a cleanup callable (for HMR, tests).
 */
import { App, type URLOpenListenerEvent } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Keyboard } from "@capacitor/keyboard";
import { Network } from "@capacitor/network";
import { consumeBackDismiss } from "./native-back";
import { isAndroid, isNative } from "./platform";
import { recordAppResume } from "./app-review";
import { applyStatusBarFloor, statusBarFloorPx } from "./status-bar-inset";

type Cleanup = () => void;

/**
 * Background timeout, in milliseconds, after which we force a biometric
 * re-auth on the next foreground. 5 minutes matches the typical bank-app
 * UX and Apple's recommendation for "sensitive" classifications.
 */
const REAUTH_AFTER_MS = 5 * 60 * 1000;

let lastBackgroundedAt: number | null = null;

/**
 * Returns `true` if the app was backgrounded longer than the re-auth
 * threshold. The auth-store reads this on resume to gate the lock
 * screen. Always `false` on web.
 */
export function shouldRequireReauthOnResume(): boolean {
  if (!isNative()) return false;
  if (lastBackgroundedAt == null) return false;
  return Date.now() - lastBackgroundedAt >= REAUTH_AFTER_MS;
}

async function setupSplashAndStatusBar(): Promise<Cleanup> {
  if (!isNative()) return () => {};
  try {
    // Background color is set via the Android theme (styles.xml statusBarColor)
    // to avoid the deprecated Window.setStatusBarColor API on Android 15+.
    // Only the icon/text style is set at runtime.
    // Navy app-bar → light status-bar icons/text.
    await StatusBar.setStyle({ style: Style.Dark });
    // overlay: true = WebView draws behind the status bar (edge-to-edge).
    // This matches setDecorFitsSystemWindows(false) in MainActivity and prevents
    // the white gap that appears between the status bar and web content when
    // overlay is false (which pushes the WebView frame down, creating a gap).
    await StatusBar.setOverlaysWebView({ overlay: true });
  } catch {
    // Ignore — old Android versions or non-supported platforms.
  }

  // CAMP-2026-0907 — the app bars clear the status bar by its MEASURED height
  // on Android (`--ch-status-bar-h`, see lib/status-bar-inset.ts): a fixed 28px
  // floor put the dashboard avatar over the battery on taller bars. Read once
  // before the splash lifts and again on rotation (the bar's height changes).
  const syncStatusBarFloor = async () => {
    try {
      const info = await StatusBar.getInfo();
      applyStatusBarFloor(statusBarFloorPx(navigator.userAgent, info.height));
    } catch {
      // Older shell without `height` — the CSS keeps its 28px floor.
    }
  };
  const onResize = () => { void syncStatusBarFloor(); };
  if (isAndroid()) {
    await syncStatusBarFloor();
    window.addEventListener("orientationchange", onResize);
  }

  // Defer the splash dismissal by one frame so the React shell has time
  // to paint. Without the delay you see a single-frame white flash
  // between splash and the dashboard skeleton.
  requestAnimationFrame(() => {
    void SplashScreen.hide({ fadeOutDuration: 250 });
  });

  return () => {
    // No teardown for the splash — Capacitor handles it.
    window.removeEventListener("orientationchange", onResize);
  };
}

/**
 * Belt-and-braces: strip any inline height/overflow the OS or a keyboard
 * plugin may have left on <html>/<body> once the keyboard is dismissed.
 *
 * With `KeyboardResize.Native` (see capacitor.config.ts) the OS owns the
 * WebView frame and there is normally nothing to clean up. But older
 * cached builds shipped `KeyboardResize.Body`, which writes an inline
 * `height` onto <body> and — on the documented show→hide race — leaves it
 * shrunk, collapsing every downstream screen to the top ~60% with a dead
 * white band below (CAMP-2026-0104 / CAMP-2026-0105). Clearing these
 * inline props on every keyboard hide guarantees the document root is
 * restored to its stylesheet-driven `min-height: 100dvh` regardless of
 * which resize mode is (or was) active. Safe to run unconditionally — it
 * only removes inline overrides, never fights the stylesheet.
 */
function restoreRootHeight(): void {
  const b = document.body.style;
  b.removeProperty("height");
  b.removeProperty("min-height");
  b.removeProperty("overflow");
  const h = document.documentElement.style;
  h.removeProperty("height");
  h.removeProperty("overflow");
}

async function setupKeyboard(): Promise<Cleanup> {
  if (!isNative()) return () => {};
  try {
    // Add a body class while the keyboard is up so input-heavy screens
    // can scroll the active field into view via CSS instead of JS.
    const showHandle = await Keyboard.addListener("keyboardWillShow", () => {
      document.documentElement.classList.add("kb-open");
    });
    const willHideHandle = await Keyboard.addListener("keyboardWillHide", () => {
      document.documentElement.classList.remove("kb-open");
    });
    // `didHide` fires AFTER the OS/plugin has finished its own resize work,
    // so this is the right moment to undo any shrunk-viewport leftovers.
    const didHideHandle = await Keyboard.addListener("keyboardDidHide", () => {
      restoreRootHeight();
    });
    return () => {
      void showHandle.remove();
      void willHideHandle.remove();
      void didHideHandle.remove();
    };
  } catch {
    return () => {};
  }
}

async function setupBackButton(navigate: (delta: number) => void): Promise<Cleanup> {
  if (!isAndroid()) return () => {};
  try {
    // On Android, the system back button is wired to the WebView by
    // default, but Capacitor lets us own the gesture.
    //
    // Order matters, and getting it wrong is CAMP-2026-0449:
    //
    //  1. Dismiss the top-most overlay, if any. Modals / bottom sheets /
    //     drawers are React state, not history entries, so popping history
    //     while one is open walks PAST the screen the user is actually looking
    //     at — which is how "back" ended up on the Dashboard from a fee detail
    //     sheet. This is also the Android platform contract: back closes what's
    //     on top before it leaves the screen.
    //  2. Otherwise pop a route, when there is one to pop.
    //  3. Otherwise minimise the app (never `exitApp()`, which force-kills the
    //     React tree and makes the next launch a cold start).
    const handle = await App.addListener("backButton", ({ canGoBack }) => {
      if (consumeBackDismiss()) return;
      if (canGoBack) {
        navigate(-1);
      } else {
        void App.minimizeApp();
      }
    });
    return () => {
      void handle.remove();
    };
  } catch {
    return () => {};
  }
}

async function setupDeepLinks(open: (path: string) => void): Promise<Cleanup> {
  if (!isNative()) return () => {};
  try {
    // Magic-login links, parent invites, payment-receipts deep-link
    // back into the app via `https://app.campushub.ai/...`. The native
    // platform's intent-filter / universal-link config (configured in
    // AndroidManifest.xml and Associated Domains entitlement) tells the
    // OS to route those URLs here. We translate them into client-side
    // navigation.
    const handle = await App.addListener("appUrlOpen", (e: URLOpenListenerEvent) => {
      try {
        const u = new URL(e.url);
        // Path + search only — drop the origin so client routing works
        // regardless of which configured host fired the deep link.
        const path = `${u.pathname}${u.search}${u.hash}`;
        if (path) open(path);
      } catch {
        // Bad URL — ignore. We don't want a malformed deep link to
        // crash the app.
      }
    });
    return () => {
      void handle.remove();
    };
  } catch {
    return () => {};
  }
}

async function setupAppStateBackground(): Promise<Cleanup> {
  if (!isNative()) return () => {};
  try {
    const stateHandle = await App.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) {
        // Going to background — start the re-auth countdown.
        lastBackgroundedAt = Date.now();
      } else if (shouldRequireReauthOnResume()) {
        // Resume after threshold — flip the global lock flag. The
        // lock-screen modal is rendered by the router-level guard
        // listening for the same event via a Zustand bridge (see
        // `useNativeLockGuard`).
        window.dispatchEvent(new CustomEvent("chai:require-reauth"));
        lastBackgroundedAt = null;
      } else if (isActive) {
        // Short background — clear the timer.
        lastBackgroundedAt = null;
      }
      // Foreground again: today counts as an active day for the store-rating policy.
      if (isActive) recordAppResume();
    });
    return () => {
      void stateHandle.remove();
    };
  } catch {
    return () => {};
  }
}

async function setupNetworkStatus(): Promise<Cleanup> {
  if (!isNative()) return () => {};
  try {
    // Network changes already drive the offline-cache flow on web via
    // the `online`/`offline` events; on native those events are flaky.
    // We re-fire our own custom event so the OfflineStatus banner
    // updates within ~1s of losing/regaining connectivity.
    const handle = await Network.addListener("networkStatusChange", (status) => {
      window.dispatchEvent(
        new CustomEvent("chai:network-status", { detail: status }),
      );
    });
    return () => {
      void handle.remove();
    };
  } catch {
    return () => {};
  }
}

/**
 * Install all native lifecycle subscribers. Idempotent — calling twice
 * is harmless (each subsystem replaces its own handlers). Returns a
 * cleanup function for tests / HMR.
 */
export async function installNativeLifecycle(opts: {
  navigate: (delta: number) => void;
  open: (path: string) => void;
}): Promise<Cleanup> {
  if (!isNative()) return () => {};

  const cleanups = await Promise.all([
    setupSplashAndStatusBar(),
    setupKeyboard(),
    setupBackButton(opts.navigate),
    setupDeepLinks(opts.open),
    setupAppStateBackground(),
    setupNetworkStatus(),
  ]);

  return () => {
    cleanups.forEach((fn) => {
      try {
        fn();
      } catch {
        // ignore
      }
    });
  };
}
